/* eslint-disable array-bracket-newline */
import { useCallback } from 'react';
import {
  formatMobileTableData,
  IMobileTableScema,
} from '../../../helpers/formatters';
import TableCardMobile from '../../../molecules/Table/mobile/TableCard';
import { formatTimestamp, formatTx } from '../formatters';
import Loader from '../../common/Loader';
import TickerCell from '../TickerCell';
import { onTxScan } from '../../../helpers/addressUtils';
import NumberDisplay from '../../../molecules/NumberDisplay';
import TableHeaderMobile from '../../../molecules/Table/mobile/TableHeader';
import {
  decorateExchangeName,
  decorateSwapType,
} from '../../../helpers/decorators';
import TokenInfo from '../../../molecules/TokenInfo';
import { IMyPosition } from './useGetMyPositions';

interface IMyPositionsTableMobileProps {
  swaps: IMyPosition[];
  loading: boolean;
  timezone?: string;
  onTradeClick(swap: IMyPosition): void;
}

const MyPositionsTableMobile = ({
  swaps,
  loading,
  timezone,
  onTradeClick,
}: IMyPositionsTableMobileProps) => {
  const schema: IMobileTableScema = {
    row_1: {
      item_1: {
        name: 'Pair',
        accessor: 'ticker',
        parser: (value) =>
          value ? (
            <TickerCell
              path={`${value.chain}-${value.address}`}
              ticker={value}
            />
          ) : (
            '-'
          ),
        width: 140,
      },
      item_2: {
        name: 'Tx Value ($)',
        accessor: 'valueUSD',
        parser: (value) => <NumberDisplay value={value} string />,
        string: true,
      },
    },
    row_2: {
      item_1: {
        name: 'Date',
        accessor: 'timestamp',
        parser: (value) => formatTimestamp(value, timezone),
        width: 140,
      },
      item_2: {
        name: 'Type',
        accessor: 'swapType',
        parser: (value) => decorateSwapType(value),
      },
    },
    row_3: {
      item_1: {
        name: 'Send',
        accessor: 'srcTokenAmount',
        parser: (value, row: IMyPosition) => (
          <TokenInfo
            chain={row.chain}
            address={row.srcTokenAddress}
            name={row.srcTokenName}
            amount={value}
            icon={row.srcTokenIcon}
          />
        ),
        width: 140,
        string: true,
      },
      item_2: {
        name: 'Receive',
        accessor: 'destTokenAmount',
        parser: (value, row: IMyPosition) => (
          <TokenInfo
            chain={row.chain}
            address={row.destTokenAddress}
            name={row.destTokenName}
            amount={value}
            icon={row.destTokenIcon}
          />
        ),
        string: true,
      },
    },
    row_4: {
      item_1: {
        name: 'Price ($)',
        accessor: 'priceUSD',
        parser: (value) => (
          <NumberDisplay value={value} endSymbol=' $' string />
        ),
        width: 140,
        string: true,
      },
      item_2: {
        name: 'Price',
        accessor: 'price',
        parser: (value, row: IMyPosition) => (
          <NumberDisplay
            value={value}
            endSymbol={` ${row.srcTokenName}`}
            string
          />
        ),
        string: true,
      },
    },
    row_5: {
      item_1: {
        name: 'Exchange',
        accessor: 'exchange',
        parser: (value, row: IMyPosition) =>
          decorateExchangeName(value, row.chain),
        width: 140,
      },
      item_2: {
        name: 'Tx Hash',
        accessor: 'tx',
        color: 'navy',
        parser: (value) => formatTx(value),
        onClick: (row) => onTxScan(row.tx, row.chain),
      },
    },
  };

  /**
   * acknowledge flipped when displaying buy/sell
   */
  const getColorForItem = useCallback(
    ({ isBuy }: IMyPosition) => (isBuy ? 'green' : 'red'),
    []
  );

  return (
    <div className='dex_trades_mobile__table'>
      {loading ? (
        <div className='centrify'>
          <Loader />
        </div>
      ) : !swaps.length ? (
        <div className='centrify'>No data found</div>
      ) : (
        <>
          <TableHeaderMobile schema={schema} />
          {formatMobileTableData({
            data: swaps,
            schema,
            getColorForItem,
            handleCardClick: onTradeClick,
            keyName: ['tx', 'order'],
          })?.map((row, index) => (
            <TableCardMobile key={index} data={row} />
          ))}
        </>
      )}
    </div>
  );
};

export default MyPositionsTableMobile;
